import { useCallback } from "react"
import { graphql, UseMutationConfig } from "react-relay"
import { type CustomMutationParams, useMutationWithErrorDialog } from "@/hooks/useMutationWithErrorDialog"
import { formatDiagnosisCodeText } from "@/modules/patients/components/diagnosis-code"
import { useChGeneratePhysiotherapyDocumentMutation as useChGeneratePhysiotherapyDocumentMutationType } from "@/modules/patients/detail/therapy-prescription/ch/services/__generated__/useChGeneratePhysiotherapyDocumentMutation.graphql"
import { getDiagnosisCodeByDiagnosisCode } from "@/modules/patients/detail/therapy-prescription/ch/services/getDiagnosisCodeByDiagnosisCode"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"

const generateSickLeaveDocumentMutation = graphql`
  mutation useChGeneratePhysiotherapyDocumentMutation($input: Ch_generatePhysiotherapyPrescriptionDocumentInput!) {
    ch_generatePhysiotherapyPrescriptionDocument(input: $input) {
      ch_PhysiotherapyPrescription {
        id
        fileId
        ...chPhysiotherapyFragment
      }
      errors {
        ... on Error {
          message
        }
      }
    }
  }
`
type Config = Omit<UseMutationConfig<useChGeneratePhysiotherapyDocumentMutationType>, "variables"> & {
  id: string
  diagnosisCode: string
  conditions: string[]
  allergies: string[]
}

const useChGeneratePhysiotherapyDocumentMutation = (params?: CustomMutationParams) => {
  const [commit, isFlight] = useMutationWithErrorDialog<useChGeneratePhysiotherapyDocumentMutationType>(
    generateSickLeaveDocumentMutation,
    params,
  )
  const patientById = usePatientParam()

  const commitHandler = useCallback(
    async (config: Config) => {
      const { id, conditions, allergies, diagnosisCode } = config

      const { code, description } = await getDiagnosisCodeByDiagnosisCode(diagnosisCode)
      const formattedDiagnosisCode = formatDiagnosisCodeText(code, description)

      commit({
        variables: {
          input: {
            id,
            patientId: patientById,
            diagnosisCode: formattedDiagnosisCode,
            conditions,
            allergies,
          },
        },
        ...config,
      })
    },
    [commit, patientById],
  )

  return [commitHandler, isFlight] as const
}

export { useChGeneratePhysiotherapyDocumentMutation }
